import { Button } from '@superops/ui-design-system';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';
import {
  EventEmitter,
  withErrorBoundary,
  getConversationOrder,
  useActivityTracker
} from '@nile/components';
import { AccountClient, CREATE_VENDOR_CONVERSATION } from '@nile/graphql';
import { useMutation } from '@apollo/client';
import { useSideConversationActionStore } from '@nile/mangifera/src/StoreManager/store/sideConversationActionStore';
import { isEmpty } from 'lodash';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';

const SendSideConversation = (props) => {
  const { data } = props;

  const isNewConversation = data?.data?.isNewConversation;

  const workItem = data?.data?.workItem;
  const conversationId = data?.data?.conversationId;
  const config = {
    module: workItem?.module,
    workId: workItem?.workId,
    conversationId: conversationId
  };

  const conversationOrder = getConversationOrder() || 'ASC';

  const editorConfig = data?.config?.editor?.storeConfig;

  const sideConversationActionStore = useSideConversationActionStore(config);
  const { template, resetTemplate } = useSuperEditor(editorConfig);

  const cc = sideConversationActionStore((state) => state.cc);
  const setCc = sideConversationActionStore((state) => state.setCc);
  const bcc = sideConversationActionStore((state) => state.bcc);
  const setBcc = sideConversationActionStore((state) => state.setBcc);
  const to = sideConversationActionStore((state) => state.to);
  const setTo = sideConversationActionStore((state) => state.setTo);
  const selectedMailbox = sideConversationActionStore(
    (state) => state.selectedMailbox
  );
  const setSelectedMailbox = sideConversationActionStore(
    (state) => state.setSelectedMailbox
  );

  const resetSideConversationStoreData = sideConversationActionStore(
    (state) => state.resetSideConversationData
  );

  const subject = sideConversationActionStore((state) => state.subject);

  const [sendConversation, { loading }] = useMutation(
    CREATE_VENDOR_CONVERSATION,
    {
      client: AccountClient,
      onCompleted: (res) => {
        if (res?.createVendorConversation) {
          if (isNewConversation) {
            useActivityTracker({
              event: `new_side_conversation_${module}`
            });
            EventEmitter.dispatch('refetchSideConversation', {
              workId: workItem?.workId
            });
          }
          resetSideConversationStoreData();
          resetTemplate();
          EventEmitter.dispatch('closeGlobalConversation', props?.data?.id);
        }
      },
      update(cache, { data }) {
        if (isNewConversation) {
          return;
        }
        const newConversation = data?.createVendorConversation;
        if (!newConversation) return;

        cache.modify({
          fields: {
            getVendorConversationsList(existing = {}, { readField }) {
              const existingConversations = existing.conversations ?? [];

              // Only update lists that belong to THIS parent conversation
              const listParentConversationId =
                existingConversations[0] &&
                readField('parentConversationId', existingConversations[0]);

              if (
                listParentConversationId &&
                listParentConversationId !==
                  newConversation.parentConversationId
              ) {
                return existing;
              }

              // Prevent duplicate insert (retry / optimistic)
              const alreadyExists = existingConversations.some(
                (ref) =>
                  readField('conversationId', ref) ===
                  newConversation.conversationId
              );

              if (alreadyExists) {
                return existing;
              }

              const newConversationRef = {
                __typename: 'Conversation',
                ...newConversation
              };

              return {
                ...existing,
                conversations:
                  conversationOrder === 'DESC'
                    ? [newConversationRef, ...existingConversations]
                    : [...existingConversations, newConversationRef]
              };
            }
          }
        });
      }
    }
  );

  const getUsers = (users) => {
    return users?.map((user) => ({ email: user.email || user?.value }));
  };

  const formatAttachments = (attachments) => {
    return attachments?.map((attachment) => {
      return {
        fileName: attachment?.fileName,
        originalFileName: attachment?.originalFileName,
        fileSize: attachment?.fileSize
      };
    });
  };

  const sendSideConversation = async () => {
    const variables = {
      input: {
        toUsers: getUsers(to),
        ccUsers: getUsers(cc),
        bccUsers: getUsers(bcc),
        content: template?.content || '',
        attachments: formatAttachments(template?.attachments),
        fromMailbox: {
          boxId: selectedMailbox?.boxId
        },
        sendMail: true,
        workItem: workItem
      }
    };
    if (isNewConversation) {
      variables.input.subject = subject?.trim();
    } else {
      variables.input.parentConversationId = conversationId;
    }
    try {
      const response = await sendConversation({
        variables
      });
      console.log(response, 'send conversation response');
    } catch (error) {
      console.error('Error sending conversation:', error);
    }
  };

  const getIsDisabled = () => {
    if (isNewConversation) {
      const currentSubject = subject ? subject.trim() : null;
      if (!currentSubject) {
        return true;
      }
      return !selectedMailbox || isEmpty(to);
    }
    return !selectedMailbox || isEmpty(to);
  };

  const isDisabled = getIsDisabled();

  return (
    <Button
      icon={<SVGIcon name="SendWhite" />}
      onClick={sendSideConversation}
      loading={loading}
      disabled={isDisabled}
      primary
    >
      <FormattedMessage id="common.send" />
    </Button>
  );
};

export default withErrorBoundary(SendSideConversation);
